import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const SUBJECT_LABEL = { math: "Matematika", czech: "Český jazyk a literatura" };
const LEVEL_LABEL = { ss_4: "SŠ / 4leté G (9. třída)", gym_6: "6leté G (7. třída)", gym_8: "8leté G (5. třída)" };

export default function TestsList() {
  const [subject, setSubject] = useState("czech");
  const [level, setLevel] = useState("gym_8");
  const [tests, setTests] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const params = {};
    if (subject !== "all") params.subject = subject;
    if (level !== "all") params.target_level = level;
    setLoading(true);
    setError("");
    api.get("/tests", { params })
      .then((r) => setTests(r.data))
      .catch((e) => { setTests([]); setError(formatApiErrorDetail(e.response?.data?.detail)); })
      .finally(() => setLoading(false));
  }, [subject, level]);

  const startTest = async (testSetId) => {
    try {
      const { data } = await api.post("/attempts/start", { test_set_id: testSetId });
      navigate(`/test/${data.attempt_id}`);
    } catch (e) {
      toast.error("Nepodařilo se spustit test");
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 animate-fade-in">
      <p className="caption mb-6">Knihovna testů</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-ink leading-tight">
        Testy <span className="font-serif-italic">nanečisto</span>.
      </h1>
      <p className="text-slate mt-4 max-w-prose text-lg">
        Dostupná je čeština pro osmiletá gymnázia (z 5. třídy). Ostatní kategorie připravujeme.
      </p>

      <p className="mt-6 text-sm text-slate">Pracovní testy k ověření: odborná revize zatím neproběhla. Z 50 bodů se 48 hodnotí automaticky; vlastní větu za 2 body zkontroluje správce. AI zatím není zapnutá.</p>
      {error && <p role="alert" className="mt-6 text-red-800">{error}</p>}
      <div className="mt-12 flex flex-wrap gap-4 items-end">
        <div className="space-y-2">
          <label className="caption">Předmět</label>
          <Select value={subject} onValueChange={setSubject}>
            <SelectTrigger className="w-[240px] bg-bone border-stone" data-testid="filter-subject">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Všechny předměty</SelectItem>
              <SelectItem value="math">Matematika — připravujeme</SelectItem>
              <SelectItem value="czech">Český jazyk</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="caption">Cílová škola</label>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="w-[260px] bg-bone border-stone" data-testid="filter-level">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Vše</SelectItem>
              <SelectItem value="ss_4">SŠ / 4leté gymnázium — připravujeme</SelectItem>
              <SelectItem value="gym_6">6leté gymnázium — připravujeme</SelectItem>
              <SelectItem value="gym_8">8leté gymnázium</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {loading ? (
          <p className="caption">Načítání…</p>
        ) : tests.length === 0 ? (
          <p className="font-serif-italic text-lg text-slate col-span-2">
            Tuto kategorii připravujeme. Dostupné testy najdeš pod Český jazyk → 8leté gymnázium.
          </p>
        ) : (
          tests.map((t) => (
            <div key={t.id} className="bg-bone border border-stone rounded-sm p-8 hover:border-navy transition-colors" data-testid={`test-card-${t.id}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <p className="caption">{SUBJECT_LABEL[t.subject]} · {LEVEL_LABEL[t.target_level]}</p>
                <Badge variant="outline">Cvičný test · pilot</Badge>
              </div>
              <h3 className="font-serif text-xl text-ink leading-snug mb-3">{t.title}</h3>
              {t.description && <p className="text-sm text-slate mb-6">{t.description}</p>}
              <div className="flex items-center gap-6 mb-6 text-sm text-slate">
                <span className="tabular font-mono">{t.question_count} otázek</span>
                <span className="tabular font-mono">{t.time_limit_minutes} min</span>
                <span className="tabular font-mono">{t.max_score} bodů</span>
              </div>
              {t.my_attempts > 0 && (
                <p className="caption mb-4">
                  Pokusy: {t.my_attempts} · nejlepší {t.my_best_score}/{t.max_score}
                </p>
              )}
              {t.in_progress_attempt_id ? (
                <Button
                  onClick={() => navigate(`/test/${t.in_progress_attempt_id}`)}
                  className="bg-navy hover:bg-navy-dark text-parchment rounded-sm"
                  data-testid={`resume-${t.id}`}>
                  Pokračovat
                </Button>
              ) : (
                <Button
                  onClick={() => startTest(t.id)}
                  className="bg-navy hover:bg-navy-dark text-parchment rounded-sm"
                  data-testid={`start-${t.id}`}>
                  Začít test
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
